import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Eye, Fuel, MapPin, AlertTriangle, Inbox, RefreshCw, Search, Bell, Radio, CheckCheck, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { PricePreviewModal } from '../admin/AdminTrackingRequests';
import { ACK_REQUIRED_TYPES } from '../../lib/notifAck';

const FUEL_ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const FUEL_LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };

const TMDashboard = () => {
  const { user } = useAuth();
  const [stations, setStations] = useState([]);
  const [competitors, setCompetitors] = useState({});
  const [unreported, setUnreported] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [previewReq, setPreviewReq] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [pushes, setPushes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, u, n, p] = await Promise.all([
        api.get('/stations').catch(() => ({ data: [] })),
        api.get('/my-competitors').catch(() => ({ data: [] })),
        api.get('/prices/unreported?min_minutes=30').catch(() => ({ data: [] })),
        api.get('/notifications', { params: { limit: 20 } }).catch(() => ({ data: [] })),
        api.get('/my-pending-pushes').catch(() => ({ data: [] })),
      ]);
      setStations((s.data || []).filter(st => st.is_own && !st.tm_marker_only));
      const cmap = {}; (c.data || []).forEach(r => { cmap[r.station_id] = r; });
      setCompetitors(cmap);
      const umap = {}; (u.data || []).forEach(r => { umap[r.station_id] = r; });
      setUnreported(umap);
      setNotifications(n.data || []);
      setPushes(p.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  const markAllRead = async () => {
    setNotifications(notifications.map(x => ({ ...x, read: true })));
    try { await api.post('/notifications/read-all'); }
    catch (e) { console.error('mark-all-read failed', e); }
  };

  const markRead = async (id) => {
    setNotifications(notifications.map(x => x.id === id ? { ...x, read: true } : x));
    try { await api.post(`/notifications/${id}/read`); }
    catch (e) { console.error('mark-read failed', e); }
  };

  // ACK_REQUIRED types for TM (paridad con agente).
  const [ackingId, setAckingId] = useState(null);

  const acknowledgeOne = async (id) => {
    setAckingId(id);
    setNotifications(notifications.map(x => x.id === id ? { ...x, read: true, acknowledged_at: new Date().toISOString() } : x));
    try { await api.post(`/notifications/${id}/acknowledge`); }
    catch (e) { console.error('acknowledge-one failed', e); }
    finally { setAckingId(null); }
  };

  const acknowledgeAll = async () => {
    const now = new Date().toISOString();
    setNotifications(notifications.map(x => ACK_REQUIRED_TYPES.has(x.type) && !x.acknowledged_at ? { ...x, read: true, acknowledged_at: now } : x));
    try { await api.post('/notifications/acknowledge-all'); }
    catch (e) { console.error('acknowledge-all failed', e); }
  };

  const ackPending = notifications.filter(n => ACK_REQUIRED_TYPES.has(n.type) && !n.acknowledged_at).length;

  const filtered = useMemo(() => {
    const out = stations.filter(s => {
      if (!q) return true;
      const t = q.toLowerCase();
      return s.name.toLowerCase().includes(t) || (s.brand || '').toLowerCase().includes(t) || (s.region || '').toLowerCase().includes(t);
    });
    // Stations with alerts first, then newest captures
    return out.sort((a, b) => {
      const ax = unreported[a.id] ? 1 : 0;
      const bx = unreported[b.id] ? 1 : 0;
      if (ax !== bx) return bx - ax;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [stations, q, unreported]);

  const totalAlerts = Object.keys(unreported).length;

  // Build a synthetic price_change_request-shaped object so we can reuse the modal.
  const openPreview = (station) => {
    const compRow = competitors[station.id];
    const comps = (compRow && compRow.competitors) || [];
    // Precios VIGENTES (oficiales + último push confirmado) — misma fuente que el
    // preview de admin y la vista móvil, para que las 3 vistas coincidan siempre.
    const vig = compRow && compRow.own_prices && Object.keys(compRow.own_prices).length ? compRow.own_prices : null;
    const our = vig || station.official_prices || {};
    // Compute diffs (top per fuel/svc, first competitor with significant delta)
    const diffs = {};
    for (const k of FUEL_ORDER) {
      const p = our[k]; if (!p) continue;
      for (const svc of ['self', 'full']) {
        const ourVal = p[svc]; if (ourVal == null) continue;
        for (const c of comps) {
          const cv = c.prices?.[k]?.[svc];
          if (cv != null && Number(cv) !== 0 && Math.abs(Number(cv) - Number(ourVal)) > 0.001) {
            diffs[`${k}.${svc}`] = { our: Number(ourVal), competitor: Number(cv), competitor_id: c.id, delta: Number((Number(cv) - Number(ourVal)).toFixed(2)) };
            break;
          }
        }
      }
    }
    setPreviewReq({
      id: `tm-preview-${station.id}`,
      station_id: station.id,
      station_name: station.name,
      station_brand: station.brand,
      requested_by_name: user?.name || 'Territory Manager',
      requested_by_role: 'territory_manager',
      requested_at: new Date().toISOString(),
      status: 'preview',  // any value != 'open' → modal renders read-only footer
      window_hours: station.min_hours_between_changes || 48,
      our_prices: our,
      competitor_snapshot: comps,
      diffs,
    });
  };

  return (
    <div className="space-y-6" data-testid="tm-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Mis estaciones</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">
            {filtered.length} estaciones · {totalAlerts > 0 ? (
              <span className="inline-flex items-center gap-1 text-[#991b1b] font-medium"><AlertTriangle size={12}/> {totalAlerts} con diferencias pendientes de reportar</span>
            ) : 'sin alertas'}
          </p>
        </div>
        <Button onClick={load} variant="outline" className="h-10 rounded-full border-[#d4cfc4] text-[12.5px]" data-testid="tm-refresh">
          <RefreshCw size={13} className="mr-1.5"/> Actualizar
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
        <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar estación, marca o región…" className="pl-9 bg-white border-[#e8e5de] rounded-full text-[13px] h-10" data-testid="tm-search"/>
      </div>

      {/* Two-column inline panel: Push de precios + Notificaciones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Push de precios activos */}
        <section className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="tm-pushes-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Radio size={14} className="text-[#1c1c1c]" strokeWidth={1.7}/>
              <h3 className="text-[14px] font-medium">Push de precios activos</h3>
            </div>
            <span className="text-[10.5px] uppercase tracking-wider text-[#8a8579]">{pushes.length}</span>
          </div>
          {pushes.length === 0 ? (
            <p className="text-[12px] text-[#8a8579] py-4 text-center">Sin pushes pendientes para tus estaciones.</p>
          ) : (
            <ul className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {pushes.map(sp => {
                const eff = new Date(sp.station_effective_at);
                const now = new Date();
                const diffH = (eff - now) / 36e5;
                const inWindow = diffH <= 0;
                return (
                  <li key={sp.id} className={`border rounded-lg p-2.5 ${inWindow ? 'border-[#fde68a] bg-[#fffbeb]' : 'border-[#e8e5de] bg-[#f8f6f1]'}`} data-testid={`tm-push-${sp.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[12.5px] font-medium leading-tight truncate">{sp.station_name}</p>
                        <p className="text-[11px] text-[#8a8579] inline-flex items-center gap-1 mt-0.5">
                          <Clock size={10.5}/>
                          {inWindow ? 'Efectivo · pendiente confirmar' : `efectivo ${eff.toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}`}
                        </p>
                        {sp.push_message && <p className="text-[10.5px] text-[#5a5a5a] mt-1 line-clamp-2">{sp.push_message}</p>}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${sp.status === 'received' ? 'bg-[#dcfce7] text-[#166534]' : sp.status === 'escalated' ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#f3f0e9] text-[#3a3a3a]'}`}>
                        {sp.status}
                      </span>
                    </div>
                    {sp.final_prices && (
                      <div className="flex flex-wrap gap-1.5 mt-2 text-[10.5px] font-mono tabular-nums text-[#3a3a3a]">
                        {Object.entries(sp.final_prices).map(([f, p]) => (
                          <span key={f} className="bg-white border border-[#e8e5de] rounded px-1.5 py-0.5">
                            {f}: Q{Number(p.self || p.base || 0).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Notificaciones recientes */}
        <section className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid="tm-notif-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-[#1c1c1c]" strokeWidth={1.7}/>
              <h3 className="text-[14px] font-medium">Notificaciones</h3>
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#b91c1c] text-white" data-testid="tm-unread-count">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </div>
            {notifications.some(n => !n.read) && (
              <button onClick={markAllRead} className="text-[11px] text-[#5a5a5a] hover:text-[#1c1c1c] inline-flex items-center gap-1" data-testid="tm-mark-all-read">
                <CheckCheck size={11}/> Marcar todas
              </button>
            )}
          </div>
          {ackPending > 0 && (
            <div className="flex items-center justify-between gap-2 bg-[#fef3c7] border border-[#fde68a] rounded-lg p-2.5 mb-2" data-testid="tm-ack-banner">
              <p className="text-[11.5px] text-[#92400e]"><strong>{ackPending}</strong> cambio{ackPending > 1 ? 's' : ''} de precio pendiente{ackPending > 1 ? 's' : ''} de confirmar visto</p>
              <Button
                onClick={acknowledgeAll}
                className="h-7 rounded-full bg-[#92400e] hover:bg-[#7c2d12] text-white text-[10.5px] px-2.5 shrink-0"
                data-testid="tm-ack-all"
              >
                Confirmar todos
              </Button>
            </div>
          )}
          {(() => {
            const visibleNotifs = notifications.filter(n =>
              ACK_REQUIRED_TYPES.has(n.type) ? !n.acknowledged_at : !n.read
            );
            if (visibleNotifs.length === 0) {
              return (
                <p className="text-[12px] text-[#8a8579] py-4 text-center">
                  {notifications.length === 0 ? 'Sin notificaciones.' : 'Todo al día.'}
                </p>
              );
            }
            return (
              <ul className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                {visibleNotifs.slice(0, 10).map(n => {
                  const needsAck = ACK_REQUIRED_TYPES.has(n.type) && !n.acknowledged_at;
                  return (
                <li key={n.id}
                    onClick={() => !n.read && markRead(n.id)}
                    className={`px-2.5 py-2 rounded-lg cursor-pointer transition border ${!n.read ? 'bg-[#fffbeb] border-[#fde68a]' : 'bg-[#f8f6f1] border-transparent hover:bg-[#f3f0e9]'}`}
                    data-testid={`tm-notif-${n.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`text-[12.5px] leading-tight ${!n.read ? 'font-medium' : 'text-[#3a3a3a]'} truncate`}>{n.title}</p>
                      {n.body && <p className="text-[11px] text-[#5a5a5a] mt-0.5 line-clamp-2">{n.body}</p>}
                      <p className="text-[10px] text-[#8a8579] mt-0.5">{new Date(n.created_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}</p>
                    </div>
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#b91c1c] mt-1.5 shrink-0"/>}
                  </div>
                  {needsAck && (
                    <div className="mt-2 flex justify-end">
                      <Button
                        onClick={(e) => { e.stopPropagation(); acknowledgeOne(n.id); }}
                        disabled={ackingId === n.id}
                        className="h-6 rounded-full bg-[#92400e] hover:bg-[#7c2d12] text-white text-[10px] px-2.5"
                        data-testid={`tm-ack-${n.id}`}
                      >
                        Confirmar visto
                      </Button>
                    </div>
                  )}
                </li>
              );})}
              </ul>
            );
          })()}
        </section>
      </div>

      {loading && filtered.length === 0 ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-12 text-center">
          <Inbox size={28} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
          <p className="text-[14px] text-[#5a5a5a]">Sin estaciones asignadas a tu zona.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(s => {
            const alert = unreported[s.id];
            const _cmp = competitors[s.id];
            const prices = (_cmp && _cmp.own_prices && Object.keys(_cmp.own_prices).length ? _cmp.own_prices : s.official_prices) || {};
            const hasFull = FUEL_ORDER.some(k => prices[k]?.full != null);
            return (
              <div
                key={s.id}
                className={`relative bg-white border rounded-xl p-5 transition ${alert ? 'border-[#fecaca] shadow-[0_0_0_3px_rgba(254,202,202,0.4)]' : 'border-[#e8e5de]'}`}
                data-testid={`tm-station-${s.id}`}
              >
                {alert && (
                  <span
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#b91c1c] alert-blink"
                    data-testid={`tm-alert-${s.id}`}
                    title={`${alert.diffs_count} diferencia${alert.diffs_count > 1 ? 's' : ''} · ${alert.minutes_since_latest_capture} min sin reportar`}
                  />
                )}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-[14.5px] font-medium leading-tight truncate">{s.name}</p>
                    <p className="text-[11.5px] text-[#8a8579] inline-flex items-center gap-1 mt-0.5"><MapPin size={11}/> {s.region}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f3f0e9] text-[#3a3a3a] shrink-0">{s.brand}</span>
                </div>

                {Object.keys(prices).length > 0 ? (
                  <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 items-center mb-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#8a8579]">Producto</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8a8579] text-right w-14">Auto</div>
                    {hasFull && <div className="text-[10px] uppercase tracking-wider text-[#8a8579] text-right w-14">Full</div>}
                    {!hasFull && <div className="w-14"/>}
                    {FUEL_ORDER.map(k => {
                      const p = prices[k]; if (!p) return null;
                      return (
                        <React.Fragment key={k}>
                          <div className="text-[12.5px]">{FUEL_LABELS[k]}</div>
                          <div className="text-right font-mono text-[12.5px] tabular-nums">{p.self != null ? `Q${Number(p.self).toFixed(2)}` : '—'}</div>
                          {hasFull && <div className="text-right font-mono text-[12.5px] tabular-nums text-[#5a5a5a]">{p.full != null ? `Q${Number(p.full).toFixed(2)}` : '—'}</div>}
                          {!hasFull && <div/>}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[11.5px] text-[#8a8579] py-2"><Fuel size={11} className="inline mr-1"/> Sin precios oficiales registrados.</p>
                )}

                {alert && (
                  <p className="text-[11px] text-[#991b1b] mb-2 inline-flex items-center gap-1">
                    <AlertTriangle size={11}/> {alert.diffs_count} diferencia{alert.diffs_count > 1 ? 's' : ''} · captura hace {alert.minutes_since_latest_capture < 60 ? `${alert.minutes_since_latest_capture}min` : `${Math.floor(alert.minutes_since_latest_capture / 60)}h`}
                  </p>
                )}

                <Button
                  type="button"
                  onClick={() => openPreview(s)}
                  className="w-full h-9 rounded-full bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black text-[12px]"
                  data-testid={`tm-preview-${s.id}`}
                >
                  <Eye size={13} className="mr-1.5"/> Preview de precios
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <PricePreviewModal
        open={!!previewReq}
        onOpenChange={(o) => !o && setPreviewReq(null)}
        request={previewReq}
        onReview={() => {}}
        busy={false}
      />
    </div>
  );
};

export default TMDashboard;
